import { getActionText } from 'copilot-toolkit/actions/getActionText';
import Raven from 'raven-js';
import { useCallback, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAblyChannelId } from '../../chat/selectors/chatv2Selectors';
import { chatV2Actions } from '../../chat/slices/chatv2';
import { getToolInputFromMessageAction } from '../../chat/utils/collabModeUtil';
import { useIsUngatedForLoadableTools } from '../../gating/hooks/useIsUngatedForLoadableTools';
import { AblyRealtimeClientContext } from '../context/AblyRealtimeClientProvider';
export const useCopilotV2PublishAblyAction = () => {
  const dispatch = useDispatch();
  const ablyRealtimeClient = useContext(AblyRealtimeClientContext);
  const ablyChannelId = useSelector(selectAblyChannelId);
  const isUngatedForLoadableTools = useIsUngatedForLoadableTools();
  const publishAblyAction = useCallback(payload => {
    if (!ablyChannelId) {
      return;
    }
    if (!payload.messageAction) {
      Raven.captureMessage('Failure publishing Ably action due to missing messageAction', {
        extra: {
          component: 'useCopilotV2PublishAblyAction'
        }
      });
      return;
    }
    const {
      isCollaborateMode,
      messageAction,
      context,
      messageTracking,
      customCopilotId,
      type,
      sourceHostAppPathname // TODO: BE is not currently using this, but we had it for v1 - determine if it is needed for tracking in v2
    } = payload;

    // some actions can have dynamic text passed by an input field
    let message = getActionText(messageAction);

    // TODO - create a single field for handling message overrides instead of having `text`/`title` and `getActionText` as a fallback
    if (isCollaborateMode && messageAction.title) {
      message = messageAction.title;
    } else if (messageAction.text) {
      message = messageAction.text;
    }
    const currentAblyChannel = ablyRealtimeClient.channels.get(ablyChannelId);

    // if undefined, the BE should gracefully handle with an error response from the LLM
    // TODO - eventually we should be more opinionated with error handling and possibly throw an error given an invalid tool input
    const toolInput = getToolInputFromMessageAction(Boolean(isCollaborateMode), messageAction, isUngatedForLoadableTools);
    const payloadWithMessage = Object.assign({}, payload, {
      message
    });
    const callbackHandler = error => {
      // null indicates successful publish
      if (error === null) {
        // TODO - handle successful case, should there be a loading state?
        // same as useCopilotV2PublishAblyMessage
      } else {
        dispatch(chatV2Actions.chatUserMessageSentException(payloadWithMessage));
      }
    };

    /**
     * @temporary This block is used for handling exceptions in V1 actions that are not supported in V2.
     * @expiration 2025-08-15
     * @deprecated
     * This happens when a valid V1 action cannot be translated to a V2 tool call
     */
    if (!toolInput) {
      // we don't want to display a user message if we don't have any relevant user-message text to use
      if (message.length) {
        dispatch(chatV2Actions.chatUserMessageSentPending(payloadWithMessage));
      }
      dispatch(chatV2Actions.chatUserMessageActionException());
    } else {
      dispatch(chatV2Actions.chatUserMessageSentPending(payloadWithMessage));
      currentAblyChannel.publish('user_action', {
        context,
        messageTracking,
        customCopilotId,
        message,
        // Only 1 tool maps per action in v1/v2 - if we want to support more tools we'll need to rework our action architecture
        toolInputs: toolInput ? [toolInput] : [],
        type,
        sourceHostAppPathname // TODO: BE is not currently using this, but we had it for v1 - determine if it is needed for tracking in v2
      }, callbackHandler);
    }
  }, [ablyChannelId, ablyRealtimeClient, dispatch, isUngatedForLoadableTools]);
  return publishAblyAction;
};